import { type FormEvent, useState } from "react";
import { addDays, type DateStr, minutesToTime, nowInZone, SCHEDULE_MIN_MS, todayInZone, zonedToInstant } from "@justone/shared";
import { formatWhen } from "../lib/format";
import { MiniCalendar } from "./DatePicker";
import { Dialog, Field } from "./ui";

/** 예약 시각 빠른 선택: 30분·1시간·4시간 뒤, 내일 오전 9시 */
export function schedulePresets(now = new Date()): { label: string; at: Date }[] {
  const after = (min: number) => new Date(now.getTime() + min * 60_000);
  return [
    { label: "30분 뒤", at: after(30) },
    { label: "1시간 뒤", at: after(60) },
    { label: "4시간 뒤", at: after(240) },
    { label: "내일 오전 9시", at: zonedToInstant(addDays(todayInZone(now), 1), "09:00") },
  ];
}

/** 날짜(달력)와 시각을 골라 예약 */
export function ScheduleDialog({ title = "예약 전송", initial, confirmLabel = "예약", onPick, onClose }: { title?: string; initial?: Date; confirmLabel?: string; onPick: (at: Date) => void; onClose: () => void }) {
  const start = initial ?? zonedToInstant(addDays(todayInZone(), 1), "09:00");
  const [date, setDate] = useState<DateStr>(todayInZone(start));
  const [time, setTime] = useState(() => minutesToTime(nowInZone(start).minutes));
  const at = /^\d{2}:\d{2}$/.test(time) ? zonedToInstant(date, time) : null;
  const tooSoon = !!at && at.getTime() - Date.now() < SCHEDULE_MIN_MS;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (at && !tooSoon) onPick(at);
  };
  return (
    <Dialog open onClose={onClose} label={title}>
      <form onSubmit={submit} className="schedule-form">
        <h2>{title}</h2>
        <MiniCalendar value={date} onPick={setDate} />
        <Field id="schedule-time" label="시각">
          <input id="schedule-time" type="time" className="input input-sm" required value={time} onChange={(e) => setTime(e.target.value)} />
        </Field>
        <p className={`schedule-preview${tooSoon ? " form-error" : ""}`} aria-live="polite">
          {!at ? "시각을 고르세요." : tooSoon ? "지금보다 1분 이상 뒤로 고르세요." : `${formatWhen(at.toISOString())}에 보냅니다.`}
        </p>
        <div className="buttons">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={!at || tooSoon}>
            {confirmLabel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
